import * as React from 'react';
import { ConfigProvider } from 'antd';
import { getLocale, useRouteMatch, useParams, history, useIntl, NavLink } from 'umi';
import { useMount } from '@umijs/hooks';
import { inject, observer } from 'mobx-react';
import RTCModel from '@/models/rtc';
import MeetingModel from '@/models/meeting';
import { iosIsUnder13, ua } from '@/utils/deviceCheck';
import { AppProvider, AppShape } from '@/utils/appProvider';
import { initMainClient } from '@/utils/NERTC/mainClient';
import { TIPS_TYPE } from '@/types/tips';
import Index from '@/pages/index';
import User from '@/models/user';
import { disableWhiteboard, schemaEncrypt, whiteboardAuth } from '@/api';
import { message } from '@/components';

import '@/styles/theme.less';
import Style from './index.less';
import { setLocale } from '@/utils/locale';
import { LoginEvent, eventBus } from '@/utils/event';
import { clearSessionStorage, getQueryString } from '@/utils/common';
import { authLogin } from '@/utils/auth';
import { LOGIN_TYPE } from '@/pages/PC/SelectLogin';
import { getLocalPassword } from '@/utils/localStorage';
import { elkReport } from '@/utils/elkReport';

const LANG = ['en', 'zh-CN', 'ja'];

interface LayoutProps {
  RTCInstance: RTCModel;
  meeting: MeetingModel;
  user: User;
}

interface ParamsStruct {
  meetingId: string;
}

const PREFIX_CLS = 'popo-meeting';
let hasUpdate = false;
const LayoutIndex: React.FC<LayoutProps> = inject(({ store }) => {
  return {
    RTCInstance: store.rtc,
    user: store.user,
    meeting: store.meeting,
    whiteboard: store.whiteboard,
  };
})(
  observer((props) => {
    const { children, RTCInstance, meeting, user } = props;
    const { formatMessage } = useIntl();
    const { meetingId } = useParams<ParamsStruct>();
    const match = useRouteMatch();
    const getPrefixCls = (prefix: string) => (suffix: string, prefixCls?: string) => {
      if (!prefixCls) {
        prefixCls = suffix ? `${prefix}-${suffix}` : prefix;
      }
      return prefixCls;
    };

    const initMeeting = async (meetingLogged: boolean) => {
      try {
        // 创建主频道
        console.log('创建主频道', new Date());
        const password = getLocalPassword(meetingId);
        if (meetingLogged) {
          const resp = await meeting.getInfo({ meetingId, password }, true);
        }
        const client = await initMainClient({
          appKey: meeting.appKey as string,
          stopScreenSharing: () => {
            RTCInstance.closeScreen();
          },
          notAllowedError: () => {
            console.log('initMeeting-主频道notAllowedError事件触发', meetingId);
            RTCInstance.setAudioPlayErr(true);
          },
          onerror: () => {
            message.error({
              content: formatMessage({ id: 'CHANGENET' }),
              duration: 0,
              close: true,
            });
          },
          connectionStateChange: (evt) => {
            console.log('connectionStateChange', evt);
            RTCInstance?.setCurState(evt.curState);
            if (
              (evt.prevState === 'CONNECTED' && evt.curState === 'CONNECTING') ||
              RTCInstance.curState === 'DISCONNECTED'
            ) {
              RTCInstance.setDisconnected(true);
            }
            if (evt.curState === 'CONNECTED') {
              if (RTCInstance.connecting) {
                RTCInstance.setReconnected(true);
              }
              RTCInstance.setConnecting(false);
              RTCInstance.setFirstConnecting(false);
            }
            if (evt.curState === 'CONNECTING') {
              RTCInstance.setConnecting(true);
              RTCInstance.setReconnected(false);
            }
          },
        });
        RTCInstance.setClient(client);
        // 创建本地流
        await RTCInstance.createLocalStream();
        // await RTCInstance.init(meeting.appKey as string);
        await RTCInstance.checkDeviceStatus();
        // await RTCInstance.enableDualStream();
      } catch (err: any) {
        console.warn(err);
        clearSessionStorage();
        elkReport({
          type: 'initMeeting创建主频道失败',
          logData: { meetingInfo: meeting?.info, err },
        });
        if ([404, 510001].includes(err.status)) {
          history.push({
            pathname: `/meet/${meetingId}/tips`,
            query: {
              type: TIPS_TYPE.NOT_FOUND,
            },
          });
        }

        // 5101 会议已失效 | 5102 未开启 web 入会开关
        if ([570004, 511003].includes(err.status)) {
          history.push({
            pathname: `/meet/${meetingId}/forbid`,
            query: {
              errorCode: err.status,
            },
          });
        }

        // 5101 会议已失效 | 5102 未开启 web 入会开关
        if ([5101, 5102, 570001, 510002].includes(err.status)) {
          history.push({
            pathname: `/meet/${meetingId}/tips`,
            query: {
              type: TIPS_TYPE.FINISHED,
            },
          });
        }
        // }
      }

      if (process.env.NODE_ENV === 'development') {
        hasUpdate = true;
      }
    };

    useMount(async () => {
      window.$meetingId = meetingId;
      function updateOnlineStatus() {
        user.setIsOnline(navigator.onLine);
      }
      window.addEventListener('online', updateOnlineStatus);
      window.addEventListener('offline', updateOnlineStatus);

      let { language } = navigator;
      if (language.includes('-')) {
        // 兼容 safari
        language = `${language.split('-')[0]}-${language.split('-')[1].toLocaleUpperCase()}`;
      }
      setLocale(language);
      if (iosIsUnder13) {
        history.push({
          pathname: `/meet/${meetingId}/tips`,
          query: {
            type: TIPS_TYPE.IOS_UNDER_13,
          },
        });
        return;
      }
      if (!history.location.pathname.match(/pwd=/i)) {
        localStorage.setItem('url', window.location.href);
      }
      let loginResp;
      // 获取认证状态
      try {
        loginResp = await user.getUserInfo(meetingId);
        window.sessionStorage.setItem('waterMark', JSON.stringify(loginResp.data?.watermark));
      } catch (err: any) {
        // 当前用户未登录
        if (err.status === 305 && !window.location.href.includes('pwd=')) {
          history.replace({
            pathname: `/meet/${meetingId}/tips`,
            query: {
              type: TIPS_TYPE.DISABLE_TOKEN,
            },
          });
          return;
        }
      }

      // 获取meetingInfo的信息，选择跳转到会议还是跳转到选择不同方式的登入页面
      try {
        const resp = await meeting.getInfo({ meetingId }, false);
        if (resp.data && resp.data.meetingName) {
          setTimeout(() => {
            document.title = resp.data.meetingName;
          }, 100);
        }
        if (loginResp?.data?.meetingLogged && user.sessionId === loginResp?.data?.sessionId) {
          history.replace({
            pathname: `/meet/${meetingId}/meeting`,
          });
        }

        if (
          loginResp?.data &&
          !loginResp?.data.openAuthed &&
          getQueryString(LOGIN_TYPE.GUEST) !== '1' &&
          window.isWeb &&
          !window.isMobile &&
          !loginResp?.data?.meetingLogged
        ) {
          history.push(`/meet/pc/selectLogin${location.search}`);
        }
      } catch (err: any) {
        if ([404, 510001].includes(err.status)) {
          history.push({
            pathname: `/meet/${meetingId}/tips`,
            query: {
              type: TIPS_TYPE.NOT_FOUND,
            },
          });
        }
        if ([5101, 5102, 570001, 510002, 511003].includes(err.status)) {
          history.push({
            pathname: `/meet/${meetingId}/tips`,
            query: {
              type: TIPS_TYPE.FINISHED,
            },
          });
        }
      }
      // 初始化会议相关
      !hasUpdate && initMeeting(loginResp?.data?.meetingLogged);
      openPopoModal();
    });
    React.useEffect(() => {
      if (!RTCInstance.cid) {
        return;
      }
      if (meeting.info?.whiteboardStatus?.wbEnabled && !meeting.whiteboardUid) {
        if (meeting.info.whiteboardStatus.wbEnableUid === user.uid) {
          disableWhiteboard(RTCInstance.cid).catch((e) => {
            console.error('failed to disable whiteboard.', e);
          });
        } else {
          whiteboardAuth(RTCInstance.cid)
            .then((res) => {
              if (
                meeting.info?.whiteboardStatus?.wbEnableUid &&
                meeting.info?.whiteboardStatus?.wbEnableDeviceId
              ) {
                meeting.setWhiteboardUid(
                  meeting.info!.whiteboardStatus!.wbEnableUid +
                    meeting.info!.whiteboardStatus!.wbEnableDeviceId,
                  {
                    appkey: res.data.appkey,
                    nonce: res.data.nonce,
                    checksum: res.data.checksum,
                    curTime: res.data.curTime,
                  },
                  res.data?.whiteboardChannelId
                );
              }
              meeting.setWhiteboardLoading(true);
            })
            .catch((e) => {
              console.error('failed to query whiteboard auth.', e);
            });
        }
      }
    }, [
      meeting,
      meeting.info?.whiteboardStatus?.wbEnabled,
      meeting.info?.whiteboardStatus?.wbEnableUid,
      RTCInstance.cid,
      user.uid,
    ]);

    const openPopoModal = () => {
      // 暂时禁用popo跳转
      // if (window.location.hostname === 'test-meet.popo.netease.com') return;
      if (process.env.NODE_ENV === 'development') {
        // 本地调试关闭打开popo的提示
        return;
      }
      if (meetingId && ua.browser.name?.match(/chrome/i)) {
        schemaEncrypt('meeting', { oper_id: meetingId, oper_type: 'join' }).catch((err) => {
          if (err.errcode === 0 && err.data) {
            function jump() {
              if (!getQueryString('pwd')) return;
              const a = window.document.createElement('a');
              a.href = err.data;
              a.click();
            }
            if (window.isWeb) {
              setTimeout(() => {
                try {
                  if (localStorage.getItem('openPopoModal') == '1') {
                    return;
                  }
                  jump();
                  if (meeting?.info?.supportedAuthTypes?.length > 1) {
                    localStorage.setItem('openPopoModal', '1');
                  }
                } catch (err) {
                  console.warn('schema打开失败', err);
                }
              }, 2000);
            } else {
              window.document.addEventListener('click', function clickJump() {
                jump();
                window.document.removeEventListener('click', clickJump);
              });
            }
          }
        });
      }
    };

    React.useEffect(() => {
      if (!RTCInstance.cid) {
        return;
      }
      if (meeting.info?.codeEditorStatus?.codeEditorEnabled) {
        meeting.setCodeEditorEnabled(true);
        meeting.setCodeEditorLoading(true);
      }
    }, [RTCInstance.cid, meeting]);

    React.useEffect(() => {
      return () => {
        RTCInstance && RTCInstance.destroy();
      };
    }, [RTCInstance]);

    React.useEffect(() => {
      return () => {
        localStorage.setItem('openPopoModal', '');
      };
    }, []);

    const context: AppShape = {
      locale: getLocale(),
      prefixCls: PREFIX_CLS,
      getPrefixCls: getPrefixCls(PREFIX_CLS),
    };

    return (
      <AppProvider value={context}>
        <ConfigProvider>
          <div className={Style['layout-page']}>
            <div className={Style['app-layout-page']}>{match.isExact ? <Index /> : children}</div>
          </div>
        </ConfigProvider>
      </AppProvider>
    );
  })
);
export default LayoutIndex;
